import axios from 'axios';

// Portal session plumbing. The backend mints a session token at magic-link
// verify; every customer-scoped API call must carry it. This module attaches
// it to all axios AND fetch requests aimed at the portal backend, and forces
// re-login when the backend says the session is missing/expired (401).

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export function getPortalToken() {
  return localStorage.getItem('portal_token') || '';
}

export function setPortalToken(token) {
  if (token) localStorage.setItem('portal_token', token);
}

export function clearPortalSession() {
  localStorage.removeItem('portal_token');
  localStorage.removeItem('customer');
}

// Start a fresh portal session right after a successful login (magic link or
// SMS code). Everything left over from whoever used this browser before is
// dropped first, so nothing — customer id, property choice, deep links, UI
// prefs — can leak from a previous customer into this one (2026-10-15).
export function startPortalSession(customer, sessionToken) {
  clearPortalSession();
  ['open_estimate', 'open_invoice', 'open_tab'].forEach((k) => localStorage.removeItem(k));
  try {
    sessionStorage.removeItem('rachio_skipped');
  } catch (e) { /* storage unavailable */ }
  localStorage.setItem('customer', JSON.stringify(customer));
  setPortalToken(sessionToken);
}

// The logged-in customer as stored at login. Read it when a page mounts (never
// once at app start-up), so a login that happens inside the SPA is always seen.
export function currentPortalCustomerId() {
  try {
    return JSON.parse(localStorage.getItem('customer') || '{}').id || null;
  } catch (e) {
    return null;
  }
}

function isPortalApiUrl(url) {
  if (typeof url !== 'string') return false;
  return (
    (BACKEND_URL && url.startsWith(`${BACKEND_URL}/api/`)) ||
    url.startsWith('/api/')
  );
}

function onUnauthorized() {
  clearPortalSession();
  if (window.location.pathname !== '/') {
    window.location.assign('/');
  }
}

// axios — covers every axios call site
axios.interceptors.request.use((config) => {
  const token = getPortalToken();
  const url = config.url || '';
  const full = config.baseURL ? config.baseURL + url : url;
  if (token && isPortalApiUrl(full) && !config.headers?.Authorization) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});
axios.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error.response?.status === 401 && isPortalApiUrl(error.config?.url || '')) {
      onUnauthorized();
    }
    return Promise.reject(error);
  }
);

// fetch — covers the direct fetch() call sites (EstimateView, LetterView, …)
const _origFetch = window.fetch.bind(window);
window.fetch = async (input, init = {}) => {
  const url = typeof input === 'string' ? input : input?.url || '';
  const token = getPortalToken();
  if (token && isPortalApiUrl(url)) {
    init = { ...init, headers: { ...(init.headers || {}), Authorization: `Bearer ${token}` } };
  }
  const resp = await _origFetch(input, init);
  if (resp.status === 401 && isPortalApiUrl(url)) {
    onUnauthorized();
  }
  return resp;
};
